import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, assertInInjectionContext, inject, Directive, Component, NgModule } from '@angular/core';
import { tap, Subject } from 'rxjs';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';

const ERROR_TRACKER_TOKEN = new InjectionToken('ERROR_TRACKER_TOKEN');
class ErrorTrackerStub {
    constructor() { }
    log(...args) {
        return this.stubbedOutTrack(args);
    }
    debug(...args) {
        return this.stubbedOutTrack(args);
    }
    info(...args) {
        return this.stubbedOutTrack(args);
    }
    warn(...args) {
        return this.stubbedOutTrack(args);
    }
    warning(...args) {
        return this.stubbedOutTrack(args);
    }
    error(...args) {
        return this.stubbedOutTrack(args);
    }
    critical(...args) {
        return this.stubbedOutTrack(args);
    }
    stubbedOutTrack(args) {
        console.warn('Using stubbed out error tracker. If you want your errors tracked, set up an error tracker.', ...args);
        return { uuid: '' };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorTrackerStub, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorTrackerStub, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorTrackerStub, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });

/* eslint-disable no-console */
const LOGGER_TOKEN = new InjectionToken('LOGGER_TOKEN');
class LoggerStub {
    constructor() { }
    log(...args) {
        console.log(...args);
    }
    trace(...args) {
        console.trace(...args);
    }
    debug(...args) {
        console.debug(...args);
    }
    info(...args) {
        console.info(...args);
    }
    warn(...args) {
        console.warn(...args);
    }
    warning(...args) {
        console.warn(...args);
    }
    error(...args) {
        console.error(...args);
    }
    critical(...args) {
        console.error(...args);
    }
    fatal(...args) {
        console.error(...args);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoggerStub, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoggerStub, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoggerStub, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });
/**
 * Convenience wrapper around @Inject(LOGGER_TOKEN) private logger: ILogger
 * Can be used as the E11 standard logger
 */
class E11Logger {
    constructor(logger) {
        this.logger = logger;
    }
    trace(message, ...additional) {
        this.logger.trace(message, ...additional);
    }
    debug(message, ...additional) {
        this.logger.debug(message, ...additional);
    }
    info(message, ...additional) {
        this.logger.info(message, ...additional);
    }
    log(message, ...additional) {
        this.logger.log(message, ...additional);
    }
    warn(message, ...additional) {
        this.logger.warn(message, ...additional);
    }
    error(message, ...additional) {
        this.logger.error(message, ...additional);
    }
    fatal(message, ...additional) {
        this.logger.fatal(message, ...additional);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11Logger, deps: [{ token: LOGGER_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11Logger, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11Logger, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_TOKEN]
                }] }] });
function e11LoggerRef() {
    assertInInjectionContext(e11LoggerRef);
    return inject(E11Logger);
}
function e11Log(e11LogConfig) {
    let { loggerRef, key } = e11LogConfig ?? {};
    if (!loggerRef) {
        assertInInjectionContext(e11Log);
        loggerRef = inject(E11Logger);
    }
    return (source) => source.pipe(tap((value) => loggerRef.log({ [`${key ?? 'log'}`]: value })));
}

class BaseE11ErrorHandler {
    constructor(logger, errorTracker) {
        this.logger = logger;
        this.errorTracker = errorTracker;
    }
    handleError(error) {
        this.trackError(error);
        return error;
    }
    trackError(error) {
        const { errorData } = error;
        const level = errorData.level ?? this.errorTrackerLevel; // level specified on the error takes precedence over the one specified on the handler
        switch (level) {
            case 'critical':
                this.errorTracker.critical(error, { errorData });
                break;
            case 'debug':
                this.errorTracker.debug(error, { errorData });
                break;
            case 'info':
                this.errorTracker.info(error, { errorData });
                break;
            case 'warning':
                this.errorTracker.warn(error, { errorData });
                break;
            case 'none':
                break;
            case 'error':
            default:
                this.errorTracker.error(error, { errorData });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseE11ErrorHandler, deps: [{ token: LOGGER_TOKEN }, { token: ERROR_TRACKER_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseE11ErrorHandler, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseE11ErrorHandler, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_TOKEN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ERROR_TRACKER_TOKEN]
                }] }] });

const STANDARD_ERROR_MESSAGE = 'Our engineers have been notified and are looking into the issue';

class DefaultE11ErrorHandler extends BaseE11ErrorHandler {
    constructor(logger, errorTracker) {
        super(logger, errorTracker);
        this.errorTrackerLevel = 'error';
    }
    // TODO: Determine how to handle errors by default
    handleError(error) {
        const errorData = error.errorData;
        this.logger.error(error, { errorObj: error });
        this.logger.debug('Error handled by default handler. Consider implementing a handler for type: ', errorData.type);
        return super.handleError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DefaultE11ErrorHandler, deps: [{ token: LOGGER_TOKEN }, { token: ERROR_TRACKER_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DefaultE11ErrorHandler, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DefaultE11ErrorHandler, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_TOKEN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ERROR_TRACKER_TOKEN]
                }] }] });

class TrackDebugE11ErrorHandler extends BaseE11ErrorHandler {
    constructor(logger, errorTracker) {
        super(logger, errorTracker);
        this.errorTrackerLevel = 'debug';
    }
    handleError(error) {
        const errorData = error.errorData;
        this.logger.warn('Sending Debug level to Error Tracker for E11 Error: ', errorData.type, { errorData });
        return super.handleError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackDebugE11ErrorHandler, deps: [{ token: LOGGER_TOKEN }, { token: ERROR_TRACKER_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackDebugE11ErrorHandler, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackDebugE11ErrorHandler, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_TOKEN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ERROR_TRACKER_TOKEN]
                }] }] });
class TrackInfoE11ErrorHandler extends BaseE11ErrorHandler {
    constructor(logger, errorTracker) {
        super(logger, errorTracker);
        this.errorTrackerLevel = 'info';
    }
    handleError(error) {
        const errorData = error.errorData;
        this.logger.warn('Sending Info level to Error Tracker for E11 Error: ', errorData.type, { errorData });
        return super.handleError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackInfoE11ErrorHandler, deps: [{ token: LOGGER_TOKEN }, { token: ERROR_TRACKER_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackInfoE11ErrorHandler, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackInfoE11ErrorHandler, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_TOKEN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ERROR_TRACKER_TOKEN]
                }] }] });
class TrackWarningE11ErrorHandler extends BaseE11ErrorHandler {
    constructor(logger, errorTracker) {
        super(logger, errorTracker);
        this.errorTrackerLevel = 'warning';
    }
    handleError(error) {
        const errorData = error.errorData;
        this.logger.warn('Sending Warning level to Error Tracker for E11 Error: ', errorData.type, { errorData });
        return super.handleError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackWarningE11ErrorHandler, deps: [{ token: LOGGER_TOKEN }, { token: ERROR_TRACKER_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackWarningE11ErrorHandler, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackWarningE11ErrorHandler, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_TOKEN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ERROR_TRACKER_TOKEN]
                }] }] });
class TrackErrorE11ErrorHandler extends BaseE11ErrorHandler {
    constructor(logger, errorTracker) {
        super(logger, errorTracker);
        this.errorTrackerLevel = 'error';
    }
    handleError(error) {
        const errorData = error.errorData;
        this.logger.warn('Sending Error level to Error Tracker for E11 Error: ', errorData.type, { errorData });
        return super.handleError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackErrorE11ErrorHandler, deps: [{ token: LOGGER_TOKEN }, { token: ERROR_TRACKER_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackErrorE11ErrorHandler, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackErrorE11ErrorHandler, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_TOKEN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ERROR_TRACKER_TOKEN]
                }] }] });
class TrackCriticalE11ErrorHandler extends BaseE11ErrorHandler {
    constructor(logger, errorTracker) {
        super(logger, errorTracker);
        this.errorTrackerLevel = 'critical';
    }
    handleError(error) {
        const errorData = error.errorData;
        this.logger.warn('Sending Critical level to Error Tracker for E11 Error: ', errorData.type, { errorData });
        return super.handleError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackCriticalE11ErrorHandler, deps: [{ token: LOGGER_TOKEN }, { token: ERROR_TRACKER_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackCriticalE11ErrorHandler, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TrackCriticalE11ErrorHandler, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_TOKEN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ERROR_TRACKER_TOKEN]
                }] }] });

const DEFAULT_ERROR_KEY = 'DEFAULT';
// TODO: Consider moving to a universal e11 lib
const E11_STANDARD_ERRORS = Object.freeze({
    // HTTP 4xx
    BAD_REQUEST: '/error/bad-request',
    UNAUTHORIZED: '/error/unauthorized',
    PAYMENT_REQUIRED: '/error/payment-required',
    FORBIDDEN: '/error/forbidden',
    NOT_FOUND: '/error/not-found',
    METHOD_NOT_ALLOWED: '/error/method-not-allowed',
    NOT_ACCEPTABLE: '/error/not-acceptable',
    PROXY_AUTHENTICATION_REQUIRED: '/error/proxy-authentication-required',
    CONFLICT: '/error/conflict',
    GONE: '/error/gone',
    LENGTH_REQUIRED: '/error/length-required',
    PRECONDITION_FAILED: '/error/precondition-failed',
    URI_TOO_LONG: '/error/uri-too-long',
    UNSUPPORTED_MEDIA_TYPE: '/error/unsupported-media-type',
    REQUESTED_RANGE_NOT_SATISFIABLE: '/error/requested-range-not-satisfiable',
    EXPECTATION_FAILED: '/error/expectation-failed',
    I_AM_A_TEAPOT: '/error/i-am-a-teapot',
    MISDIRECTED: '/error/misdirected',
    UNPROCESSABLE_ENTITY: '/error/unprocessable-entity',
    FAILED_DEPENDENCY: '/error/failed-dependency',
    PRECONDITION_REQUIRED: '/error/precondition-required',
    TOO_MANY_REQUESTS: '/error/too-many-requests',
    // HTTP 5xx
    UNKNOWN: '/error/unknown',
    INTERNAL_SERVER_ERROR: '/error/internal-server-error',
    REQUEST_TIMEOUT: '/error/request-timeout',
    BAD_GATEWAY: '/error/bad-gateway',
    SERVICE_UNAVAILABLE: '/error/service-unavailable',
    GATEWAY_TIMEOUT: '/error/gateway-timeout',
    HTTP_VERSION_NOT_SUPPORTED: '/error/http-version-not-supported',
    // Standard web errors
    MISSING_PERMISSION: '/error/missing-permission',
    // Global Errors
    NETWORK_ERROR: 'global-error/network-error',
    CHUNK_LOAD_ERROR: 'global-error/load-chunk',
    // E11 Rest Errors
    REST_VALIDATION: `rest/validation`,
});
// Default handlers for standard HTTP errors
const defaultErrorHandlerMap = {
    [DEFAULT_ERROR_KEY]: DefaultE11ErrorHandler,
    // Warning level
    [E11_STANDARD_ERRORS.BAD_REQUEST]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.UNAUTHORIZED]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.PAYMENT_REQUIRED]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.FORBIDDEN]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.NOT_FOUND]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.METHOD_NOT_ALLOWED]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.NOT_ACCEPTABLE]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.PROXY_AUTHENTICATION_REQUIRED]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.CONFLICT]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.GONE]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.LENGTH_REQUIRED]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.PRECONDITION_FAILED]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.URI_TOO_LONG]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.UNSUPPORTED_MEDIA_TYPE]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.REQUESTED_RANGE_NOT_SATISFIABLE]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.EXPECTATION_FAILED]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.MISDIRECTED]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.UNPROCESSABLE_ENTITY]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.FAILED_DEPENDENCY]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.PRECONDITION_REQUIRED]: TrackWarningE11ErrorHandler,
    [E11_STANDARD_ERRORS.TOO_MANY_REQUESTS]: TrackWarningE11ErrorHandler,
    // Error level
    [E11_STANDARD_ERRORS.UNKNOWN]: TrackErrorE11ErrorHandler,
    [E11_STANDARD_ERRORS.INTERNAL_SERVER_ERROR]: TrackErrorE11ErrorHandler,
    [E11_STANDARD_ERRORS.REQUEST_TIMEOUT]: TrackErrorE11ErrorHandler,
    [E11_STANDARD_ERRORS.BAD_GATEWAY]: TrackErrorE11ErrorHandler,
    [E11_STANDARD_ERRORS.SERVICE_UNAVAILABLE]: TrackErrorE11ErrorHandler,
    [E11_STANDARD_ERRORS.GATEWAY_TIMEOUT]: TrackErrorE11ErrorHandler,
    [E11_STANDARD_ERRORS.HTTP_VERSION_NOT_SUPPORTED]: TrackErrorE11ErrorHandler,
    [E11_STANDARD_ERRORS.MISSING_PERMISSION]: TrackErrorE11ErrorHandler,
    // Critical level
    [E11_STANDARD_ERRORS.I_AM_A_TEAPOT]: TrackCriticalE11ErrorHandler,
};
const ERROR_HANDLER_OVERRIDES_TOKEN = new InjectionToken('ErrorTypeHandlerMapping');

class E11ErrorHandlerMapService {
    constructor(handlerTypeOverrides) {
        this.handlerTypeMap = { ...defaultErrorHandlerMap, ...handlerTypeOverrides };
    }
    /**
     * Returns whether the given error type has a handler
     * This can be useful to specify a specific error handler for errors from a specific endpoint or operation that are unhandled
     * e.g. some operations should ALWAYS show a notification unless explicitly handled
     * @param type
     * @returns
     */
    isHandledErrorType(type) {
        return Object.keys(this.handlerTypeMap).includes(type);
    }
    /**
     * override the current error handlers for any error types
     * ! be careful about the order you do this in - you might override in the wrong order and get unexpected results
     * @param overrides
     */
    overrideHandlers(overrides) {
        Object.keys(overrides).forEach((type) => (this.handlerTypeMap[type] = overrides[type]));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorHandlerMapService, deps: [{ token: ERROR_HANDLER_OVERRIDES_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorHandlerMapService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorHandlerMapService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [ERROR_HANDLER_OVERRIDES_TOKEN]
                }] }] });

class E11ErrorHandlerFactory {
    constructor(errorHandlerMapService, injector) {
        this.errorHandlerMapService = errorHandlerMapService;
        this.injector = injector;
    }
    getHandlerForError(err) {
        const errorType = err.errorData.type;
        if (!errorType) {
            throw new Error(`No response or no error type found on E11Error. You must check the error is an E11Error. Message: ${err.message}`);
        }
        const handlerMap = this.errorHandlerMapService.handlerTypeMap;
        const handler = handlerMap[errorType] ?? handlerMap[DEFAULT_ERROR_KEY];
        return this.injector.get(handler);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorHandlerFactory, deps: [{ token: E11ErrorHandlerMapService }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorHandlerFactory, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorHandlerFactory, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: E11ErrorHandlerMapService }, { type: i0.Injector }] });

function isE11Error(error) {
    return !!error?.errorData?.type;
}
/**
 *
 * @param error
 * @param errorData source will default to web unless overwritten. Timestamp will default to now unless overwritten. message will default to title
 * @returns
 */
function toE11Error(e, errorData) {
    const timestamp = new Date().toISOString();
    const message = errorData.message ?? errorData.title;
    let previousErrorData = undefined;
    if (isE11Error(e))
        previousErrorData = { ...e.errorData.previousErrorData, [e.errorData.type]: e.errorData };
    const errorDataToUse = { source: 'web', timestamp, message, ...errorData, previousErrorData };
    e.errorData = errorDataToUse;
    return e;
}
function e11Error(errorData) {
    return toE11Error(new Error(errorData.title), errorData);
}
function isE11PromiseError(error) {
    return isE11Error(error.rejection);
}
function convertIfE11PromiseError(error) {
    if (isE11PromiseError(error)) {
        error.errorData = error.rejection.errorData;
    }
    return error;
}
function isE11ErrorOfType(error, type) {
    if (isE11Error(error))
        return error.errorData.type === type;
    return false;
}

const GLOBAL_ERROR_MAPPER_TOKEN = new InjectionToken('GLOBAL_ERROR_MAPPER_TOKEN');
class BaseGlobalErrorMapper {
    transform(error) {
        return toE11Error(error, this.getErrorData(error));
    }
}
class LoadChunkErrorMapper extends BaseGlobalErrorMapper {
    constructor() {
        super(...arguments);
        this.typeTester = /Loading chunk [\d]+ failed/;
    }
    getErrorData(error) {
        return {
            level: 'none',
            type: E11_STANDARD_ERRORS.CHUNK_LOAD_ERROR,
            title: 'Load chunk Error',
            message: error.message,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoadChunkErrorMapper, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoadChunkErrorMapper, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoadChunkErrorMapper, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });
class NetworkErrorMapper extends BaseGlobalErrorMapper {
    constructor() {
        super(...arguments);
        this.typeTester = new RegExp(/Network Error/i);
    }
    getErrorData(error) {
        return {
            level: 'none',
            type: E11_STANDARD_ERRORS.NETWORK_ERROR,
            title: 'Network Error. Please check your internet connection.',
            message: error.message,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NetworkErrorMapper, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NetworkErrorMapper, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NetworkErrorMapper, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const ALERT_SERVICE_TOKEN = new InjectionToken('ALERT_SERVICE_TOKEN');
class E11ErrorAlertService {
    constructor(alertService) {
        this.alertService = alertService;
    }
    alertUser(error) {
        return this.alertService.alertUser(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorAlertService, deps: [{ token: ALERT_SERVICE_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorAlertService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorAlertService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [ALERT_SERVICE_TOKEN]
                }] }] });
class ErrorAlertServiceStub {
    constructor(logger) {
        this.logger = logger;
    }
    alertUser(error) {
        this.logger.warn('Using stubbed out error alert service. It is recommended to configure a custom error alert system.');
        if (isE11Error(error))
            alert(error.errorData.title);
        else
            alert(`An error has occurred. ${STANDARD_ERROR_MESSAGE}`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorAlertServiceStub, deps: [{ token: E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorAlertServiceStub, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorAlertServiceStub, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: E11Logger }] });

class E11ErrorHandlerService {
    constructor(factory, logger, errorTracker, globalErrorMapper, alertService) {
        this.factory = factory;
        this.logger = logger;
        this.errorTracker = errorTracker;
        this.globalErrorMapper = globalErrorMapper;
        this.alertService = alertService;
    }
    /**
     * Handles Errors in a standard way - will use E11Error handlers for E11Errors
     * @param error
     * @param overrides allows the caller to override details on how the error is handled
     */
    handleError(error, overrides) {
        // Force E11 errors embedded in uncaught promises to be e11 errors
        convertIfE11PromiseError(error);
        if (isE11Error(error))
            return this.handleE11Error(error, overrides);
        // TODO: Simplify code
        const convertedGlobalError = this.convertIfGlobalError(error);
        if (isE11Error(convertedGlobalError))
            return this.handleE11Error(convertedGlobalError, overrides);
        this.logger.error(error, { errorObj: error });
        this.logger.warn('Error is non-standard and should be converted into an E11 standard error');
        this.errorTracker.error(error);
        if (overrides?.alertUser)
            this.alertService.alertUser(error);
        return error;
    }
    convertIfGlobalError(error) {
        // check if a global error mapper exists
        const globalMappers = this.globalErrorMapper.filter((mapper) => mapper.typeTester.test(error.message));
        if (!globalMappers.length)
            return error;
        if (globalMappers.length > 1) {
            this.error(`Warn: duplicate global error mapper for message ${error.message}`, globalMappers.map((v) => v.typeTester));
        }
        return globalMappers[0].transform(error);
    }
    /**
     * map function designed to return non-erroring items and handle erroring items
     * motivation: if one item errors inside a map function (e.g. due to bad data) we should return what we can and log what we can't, not error out the whole thing
     * @param arr
     * @param fun
     * @param errorDataFn should provide @E11ErrorData given an item causing a non-e11 error. Existing e11 errors will not use this
     * @returns
     */
    errorSafeMap(arr, fun, errorDataFn) {
        const newArr = [];
        arr.forEach((item) => {
            try {
                newArr.push(fun(item));
            }
            catch (e) {
                if (isE11Error(e)) {
                    this.handleE11Error(e);
                }
                else {
                    const e11Error = toE11Error(e, errorDataFn(item));
                    this.handleE11Error(e11Error);
                }
            }
        });
        return newArr;
    }
    /**
     * Handle E11Errors
     * @param error an E11Error
     * @returns
     */
    handleE11Error(error, overrides) {
        error.errorData = { ...error.errorData, ...overrides }; // TODO: is this the best way to apply overrides?
        this.logger.debug('Handling e11 error: ', { errorObj: error }); // TODO: revisit how to display handled errors in the log
        if (error.errorData.alertUser)
            this.alertService.alertUser(error);
        return this.factory.getHandlerForError(error).handleError(error);
    }
    /**
     * De-structures promises into { res?: T; err?: E11Error }
     * Converts errors into e11 errors
     * Runs error handler logic on these errors to guarantee they are handled - thus they do not need to be handled by the caller unless they have specific logic
     * To avoid tracking an error, pass `level: none` into the error data
     *
     * @param fn
     * @param errFn should provide error data given any error
     * @returns
     */
    async asyncHandler(fn, errFn) {
        try {
            return { res: await fn };
        }
        catch (err) {
            if (!errFn)
                errFn = (err) => ({ type: DEFAULT_ERROR_KEY, title: err.message });
            const e11Error = toE11Error(err, errFn(err));
            return { err: this.handleE11Error(e11Error) }; // To guarantee the error is handled
        }
    }
    /**
     * logs and sends to error tracker
     * convenience method
     * @param message
     * @param additional
     */
    log(message, ...additional) {
        this.logger.log(message, ...additional);
        this.errorTracker.log(message, ...additional);
    }
    /**
     * logs and sends to error tracker
     * convenience method
     * @param message
     * @param additional
     */
    warn(message, ...additional) {
        this.logger.warn(message, ...additional);
        this.errorTracker.warn(message, ...additional);
    }
    /**
     * logs and sends to error tracker
     * convenience method
     * @param message
     * @param additional
     */
    error(message, ...additional) {
        this.logger.error(message, ...additional);
        this.errorTracker.error(message, ...additional);
    }
    /**
     * logs and sends to error tracker
     * convenience method
     * @param message
     * @param additional
     */
    info(message, ...additional) {
        this.logger.info(message, ...additional);
        this.errorTracker.info(message, ...additional);
    }
    critical(message, ...additional) {
        this.logger.error(message, ...additional);
        this.errorTracker.critical(message, ...additional);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorHandlerService, deps: [{ token: E11ErrorHandlerFactory }, { token: LOGGER_TOKEN }, { token: ERROR_TRACKER_TOKEN }, { token: GLOBAL_ERROR_MAPPER_TOKEN }, { token: ALERT_SERVICE_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorHandlerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorHandlerService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: E11ErrorHandlerFactory }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_TOKEN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ERROR_TRACKER_TOKEN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [GLOBAL_ERROR_MAPPER_TOKEN]
                }] }, { type: E11ErrorAlertService, decorators: [{
                    type: Inject,
                    args: [ALERT_SERVICE_TOKEN]
                }] }] });

class E11ErrorBoundaryService {
    constructor() {
        this.errorNotifier = new Subject();
    }
    notifyError(component, error) {
        this.errorNotifier.next({ component, error });
    }
    getErrorNotifier() {
        return this.errorNotifier.asObservable();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorBoundaryService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorBoundaryService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorBoundaryService, decorators: [{
            type: Injectable
        }] });

/**
 * A decorator to catch and handle errors in Angular components.
 *
 * This decorator wraps all methods of the decorated component and intercepts errors thrown
 * during their execution. It requires an instance of `E11ErrorBoundaryService` to track and notify
 * errors. The decorator also integrates with Angular's `ngOnInit` lifecycle hook and detects
 * errors occurring in the Angular zone.
 *
 * ### Usage
 * To use this decorator, inject `E11ErrorBoundaryService` into the component constructor:
 *
 * ```typescript
 *
 * @CatchErrors()
 * @Component({
 *   selector: 'app-example',
 *   template: ``
 * })
 * export class ExampleComponent {
 *   constructor(public errorBoundaryService: E11ErrorBoundaryService) {}
 *
 *   someMethod() {
 *     throw new Error('This error will be tracked by E11ErrorBoundaryService');
 *   }
 * }
 * ```
 *
 * @template T The type of the Angular component being decorated.
 * @returns A class decorator for the specified component.
 */
function CatchErrors() {
    return function (target) {
        const { prototype, constructor } = target;
        const originalOnInit = prototype.ngOnInit;
        prototype.ngOnInit = function () {
            if (originalOnInit)
                originalOnInit.apply(this);
            const errorBoundaryServiceKey = findErrorTrackerServiceKey(this);
            if (!errorBoundaryServiceKey)
                return logMissingServiceWarning(target.name);
            if (this.zone) {
                this.zone.onError.subscribe((error) => {
                    // @TODO: Implement Change detection cycle errors
                    // this[errorTrackerServiceKey].notifyError(this, error)
                });
            }
            this.errorTrackerServiceKey = errorBoundaryServiceKey;
        };
        Object.getOwnPropertyNames(prototype).forEach((method) => {
            if (typeof prototype[method] === 'function' && method !== 'constructor') {
                const originalMethod = prototype[method];
                prototype[method] = function (...args) {
                    try {
                        return originalMethod.apply(this, args);
                    }
                    catch (error) {
                        const errorTrackerServiceKey = this.errorTrackerServiceKey || findErrorTrackerServiceKey(this);
                        if (errorTrackerServiceKey) {
                            this[errorTrackerServiceKey].notifyError(this, error);
                        }
                        else {
                            logMissingServiceWarning(constructor.name);
                        }
                        throw error;
                    }
                };
            }
        });
    };
    function findErrorTrackerServiceKey(instance) {
        return Object.keys(instance).find((key) => instance[key] instanceof E11ErrorBoundaryService);
    }
    function logMissingServiceWarning(componentName) {
        console.warn(`[@CatchErrors] Warning: E11ErrorBoundaryService is not available in '${componentName}'. 
         Ensure that E11ErrorBoundaryService is injected into this component. 
         Example:
         
         constructor(public E11ErrorBoundaryService: E11ErrorBoundaryService) {}
  
         This is required for proper error boundary handling within this component.`);
    }
}

class CatchTemplateErrorsDirective {
    constructor(injector) {
        this.injector = injector;
        this.errorTracker = this.injector.get(E11ErrorBoundaryService, null);
    }
    handleTemplateError(error) {
        if (this.errorTracker) {
            this.errorTracker.notifyError(null, error);
        }
        else {
            console.warn('No E11ErrorBoundaryService found for template error:', error);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CatchTemplateErrorsDirective, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: CatchTemplateErrorsDirective, selector: "[catchTemplateErrors]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CatchTemplateErrorsDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[catchTemplateErrors]',
                }]
        }], ctorParameters: () => [{ type: i0.Injector }] });

class ErrorBoundaryRegistryService {
    constructor() {
        this.registry = [];
    }
    register(boundary, tracker) {
        this.registry.push({ boundary, tracker });
    }
    unregister(boundary) {
        this.registry = this.registry.filter((entry) => entry.boundary !== boundary);
    }
    getClosestBoundary() {
        return this.registry.length > 0 ? this.registry[this.registry.length - 1].tracker : null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorBoundaryRegistryService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorBoundaryRegistryService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorBoundaryRegistryService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class E11ErrorBoundaryComponent {
    constructor(errorTracker, registry, cdr) {
        this.errorTracker = errorTracker;
        this.registry = registry;
        this.cdr = cdr;
        this.hasError = false;
    }
    ngOnInit() {
        this.registry.register(this, this.errorTracker);
        this.errorTracker.getErrorNotifier().subscribe(({ component, error }) => {
            this.hasError = true;
            this.cdr.detectChanges();
        });
    }
    ngOnDestroy() {
        this.registry.unregister(this);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorBoundaryComponent, deps: [{ token: E11ErrorBoundaryService }, { token: ErrorBoundaryRegistryService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11ErrorBoundaryComponent, selector: "e11-error-boundary", providers: [E11ErrorBoundaryService], ngImport: i0, template: `
    <ng-container *ngIf="!hasError; else fallbackContent">
      <ng-content></ng-content>
    </ng-container>
    <ng-template #fallbackContent>
      <ng-content select="[fallback]"></ng-content>
    </ng-template>
  `, isInline: true, dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorBoundaryComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-error-boundary',
                    template: `
    <ng-container *ngIf="!hasError; else fallbackContent">
      <ng-content></ng-content>
    </ng-container>
    <ng-template #fallbackContent>
      <ng-content select="[fallback]"></ng-content>
    </ng-template>
  `,
                    providers: [E11ErrorBoundaryService],
                }]
        }], ctorParameters: () => [{ type: E11ErrorBoundaryService }, { type: ErrorBoundaryRegistryService }, { type: i0.ChangeDetectorRef }] });

class E11ErrorModule {
    static forRoot(config) {
        const defaults = {
            logger: LoggerStub,
            errorHandlerMap: {},
            errorTrackerFactory: () => new ErrorTrackerStub(),
            alertService: ErrorAlertServiceStub,
            globalErrorMapper: [LoadChunkErrorMapper, NetworkErrorMapper],
        };
        const globalMappers = defaults.globalErrorMapper.concat(config?.globalErrorMapper || []);
        return {
            ngModule: E11ErrorModule,
            providers: [
                { provide: LOGGER_TOKEN, useClass: config?.logger ?? defaults.logger },
                { provide: ALERT_SERVICE_TOKEN, useClass: config?.alertService ?? defaults.alertService },
                { provide: ERROR_TRACKER_TOKEN, useFactory: config?.errorTrackerFactory ?? defaults.errorTrackerFactory },
                { provide: ERROR_HANDLER_OVERRIDES_TOKEN, useValue: config?.errorHandlerMap ?? defaults.errorHandlerMap },
                globalMappers.map(transform),
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorModule, declarations: [CatchTemplateErrorsDirective, E11ErrorBoundaryComponent], imports: [CommonModule], exports: [CatchTemplateErrorsDirective, E11ErrorBoundaryComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ErrorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CatchTemplateErrorsDirective, E11ErrorBoundaryComponent],
                    imports: [CommonModule],
                    exports: [CatchTemplateErrorsDirective, E11ErrorBoundaryComponent],
                }]
        }] });
const transform = (globalErrorMapperConfigProvider) => ({
    multi: true,
    provide: GLOBAL_ERROR_MAPPER_TOKEN,
    useClass: globalErrorMapperConfigProvider,
});

class GlobalErrorHandler {
    constructor(e11ErrorHandlerService, logger) {
        this.e11ErrorHandlerService = e11ErrorHandlerService;
        this.logger = logger;
    }
    handleError(error) {
        // check for new version(check error)
        const chunkFailedMessage = /Loading chunk [\d]+ failed/;
        if (chunkFailedMessage.test(error.message)) {
            window.location.reload();
        }
        this.e11ErrorHandlerService.handleError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: GlobalErrorHandler, deps: [{ token: E11ErrorHandlerService }, { token: LOGGER_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: GlobalErrorHandler }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: GlobalErrorHandler, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: E11ErrorHandlerService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_TOKEN]
                }] }] });

class NoOpE11ErrorHandler extends BaseE11ErrorHandler {
    constructor(logger, errorTracker) {
        super(logger, errorTracker);
        this.errorTrackerLevel = 'none';
    }
    handleError(error) {
        const errorData = error.errorData;
        this.logger.trace('Error handled by NoOpE11ErrorHandler', errorData.type, { errorData });
        return super.handleError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NoOpE11ErrorHandler, deps: [{ token: LOGGER_TOKEN }, { token: ERROR_TRACKER_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NoOpE11ErrorHandler, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NoOpE11ErrorHandler, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_TOKEN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ERROR_TRACKER_TOKEN]
                }] }] });

/*
 * Public API Surface of web-api-error
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ALERT_SERVICE_TOKEN, BaseE11ErrorHandler, BaseGlobalErrorMapper, CatchErrors, CatchTemplateErrorsDirective, DEFAULT_ERROR_KEY, DefaultE11ErrorHandler, E11ErrorAlertService, E11ErrorBoundaryComponent, E11ErrorBoundaryService, E11ErrorHandlerFactory, E11ErrorHandlerService, E11ErrorModule, E11Logger, E11_STANDARD_ERRORS, ERROR_HANDLER_OVERRIDES_TOKEN, ERROR_TRACKER_TOKEN, ErrorAlertServiceStub, ErrorBoundaryRegistryService, ErrorTrackerStub, GLOBAL_ERROR_MAPPER_TOKEN, GlobalErrorHandler, LOGGER_TOKEN, LoadChunkErrorMapper, LoggerStub, NetworkErrorMapper, NoOpE11ErrorHandler, STANDARD_ERROR_MESSAGE, TrackCriticalE11ErrorHandler, TrackDebugE11ErrorHandler, TrackErrorE11ErrorHandler, TrackInfoE11ErrorHandler, TrackWarningE11ErrorHandler, convertIfE11PromiseError, defaultErrorHandlerMap, e11Error, e11Log, e11LoggerRef, isE11Error, isE11ErrorOfType, isE11PromiseError, toE11Error };
